import React from 'react';
import {Grid} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import DateFnsUtils from '@date-io/date-fns';
import 'date-fns';
import {KeyboardDatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import {Parameter} from '@/Core/mobx/ParameterStore/Parameter';

type FormattedDatePickerProps = {
    valueChanged: (p?: Parameter & {name?: string}, value?: any, name?: string) => void;
    param: Parameter;
    label: React.ReactNode;
};

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Reusable single calendar date picker rendered as an outlined field matching
 * the TextBox / formatted-input style, with a working calendar popover. Stores a
 * `yyyy-MM-dd` string on the given parameter and parses it back to a local Date
 * for display.
 */
const FormattedDatePicker = observer(({param, valueChanged, label}: FormattedDatePickerProps) => {
    const storedValue = param?.value;
    // Append a local midnight time so the string is parsed in local time, not UTC.
    const dateValue = storedValue ? new Date(`${storedValue}T00:00:00`) : null;

    const handleDateChange = (date: Date | null) => {
        if (date === null) {
            valueChanged(param, '', param.id);
            return;
        }
        // Ignore partial/invalid input while typing; only persist a valid date.
        if (date instanceof Date && !isNaN(date.getTime())) {
            const formatted = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
            valueChanged(param, formatted, param.id);
        }
    };

    return (
        <Grid item xs={4}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                    id={param.id}
                    inputVariant="outlined"
                    variant="inline"
                    format="MM/dd/yyyy"
                    placeholder="MM/dd/yyyy"
                    label={label}
                    value={dateValue}
                    onChange={handleDateChange}
                    style={{width: '100%', marginTop: 8}}
                    InputLabelProps={{shrink: true}}
                    KeyboardButtonProps={{'aria-label': 'change date'}}
                    autoOk
                    disableToolbar
                />
            </MuiPickersUtilsProvider>
        </Grid>
    );
});

export default FormattedDatePicker;
